import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { messagingApi } from "./api";
import type {
  SendMessageRequest,
  MessageFormData,
  MessagesHistoryResponse,
  MessageChannel,
  BulkMessageRequest,
  BulkMessageFormData,
} from "./types";
import { toast } from "@shared/ui/use-toast";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  transformToSingleMessagePayload,
  transformToBulkMessagePayload,
} from "./utils";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const useRecipientTypes = () => {
  return useSmartQuery(
    QueryKeyFactory.module.communication.recipientType(),
    () => messagingApi.getRecipientTypes()
  );
};
export const useAttachTemplates = (channelType: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.communication.templateUse(utilityId, channelType),
    () => messagingApi.getTemplates(utilityId, channelType)
  );
};
export const useRecipientList = (audienceType: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    // ✅ Include audienceType in the query key so it updates when audienceType changes
    [QueryKeyFactory.module.communication.recipientList(), audienceType],
    () => messagingApi.getRecipientLists(utilityId!, audienceType)
  );
};
export const useChannelType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.communication.channelType(),
    () => messagingApi.getChannelTypes(),
    {
      staleTime: 15 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};
export const useSendMessage = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartMutation(
    async (formData: MessageFormData) => {
      if (!utilityId) {
        throw new Error("Utility ID is missing");
      }

      // Transform form data to API format
      const apiPayload = transformToSingleMessagePayload(formData, utilityId);

      // Call API
      return messagingApi.sendMessage(apiPayload);
    },
    {
      invalidateKeys: [
        QueryKeyFactory.module.communication.overview(),
        QueryKeyFactory.module.communication.channelStat(),
      ],
      onSuccess: () => {
toast({
  title: "Success",
  description: "Message sent successfully!",
  variant: "default",
});
      },
      onError: (error: any) => {
toast({
  title: "Error",
  description: error?.message || "Failed to send message",
  variant: "destructive",
});
      },
    }
  );
};

// hooks/useSendBulkMessage.ts
export const useSendBulkMessage = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartMutation(
    async (formData: BulkMessageFormData) => {
      if (!utilityId) {
        throw new Error("Utility ID is missing");
      }

      // Transform form data to API format
      const apiPayload = transformToBulkMessagePayload(formData, utilityId);

      // Call API
      return messagingApi.sendBulkMessage(apiPayload);
    },
    {
      invalidateKeys: [
        QueryKeyFactory.module.communication.overview(),
        QueryKeyFactory.module.communication.workflowStats(),
      ],
      onSuccess: () => {
toast({
  title: "Success",
  description: "Bulk message sent successfully!",
  variant: "default",
});
      },
      onError: (error: any) => {
toast({
  title: "Error",
  description: error?.message || "Failed to send bulk message",
  variant: "destructive",
});
      },
    }
  );
};

export const useMessaging = () => {
  // Helper function to get channel-specific validation
  const getChannelConstraints = (channel: MessageChannel) => {
    const constraints = {
      EMAIL: {
        requiresEmail: true,
        requiresMobile: false,
        maxSubjectLength: 200,
        maxBodyLength: 10000,
      },
      SMS: {
        requiresEmail: false,
        requiresMobile: true,
        maxSubjectLength: 0, // SMS doesn't use subject
        maxBodyLength: 160,
      },
      WHATSAPP: {
        requiresEmail: false,
        requiresMobile: true,
        maxSubjectLength: 0,
        maxBodyLength: 1024,
      },
      IN_APP: {
        requiresEmail: false,
        requiresMobile: false,
        maxSubjectLength: 100,
        maxBodyLength: 500,
      },
    };

    return constraints[channel] || constraints.EMAIL;
  };

  return {
    getChannelConstraints,
  };
};
